import type { Order, Worker } from '../types/index.js'
import type { 
  DailyTimesheet,
  TimesheetHistoryItem
} from '../contracts/timesheet.js'
import type { ApiClient, DevUtilities } from '../types/api.js'
import { 
  CreateOrderRequestSchema, 
  UpdateOrderRequestSchema, 
  debugValidation
} from '../contracts/index.js'
import type { 
  CreateOrderRequest, 
  UpdateOrderRequest,
  OrderResponse
} from '../contracts/orders.js'
import type { LoginResponse } from '../contracts/index.js'
import { getProductById } from '../data/index'
import { getAllTestUsers, validateTestPassword } from '../../tests/shared/test-users.js'

// ✅ TRACK SUBMISSION STATE: Track timesheet submissions to make history dynamic
const submissionTracker = new Map<string, {
  status: 'draft' | 'submitted' | 'approved' | 'rejected'
  submittedAt?: string
  lastSavedAt?: string
}>()

// Mock worker database for timetracker - Real names from handwritten timesheet
const mockWorkers: Worker[] = [
  // Workers currently on today's timesheet (6 names)
  {
    id: 1,
    name: 'Diana Flores',
    normalizedName: 'diana flores',
    firstSeen: '2024-01-01',
    lastSeen: '2024-12-21',
    totalDaysWorked: 85,
    status: 'active',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-12-21T00:00:00Z'
  },
  {
    id: 2,
    name: 'Henry Picado',
    normalizedName: 'henry picado',
    firstSeen: '2024-01-15',
    lastSeen: '2024-12-21',
    totalDaysWorked: 78,
    status: 'active',
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: '2024-12-21T00:00:00Z'
  },
  {
    id: 3,
    name: 'Nelson Perez',
    normalizedName: 'nelson perez',
    firstSeen: '2024-02-01',
    lastSeen: '2024-12-21',
    totalDaysWorked: 72,
    status: 'active',
    createdAt: '2024-02-01T00:00:00Z',
    updatedAt: '2024-12-21T00:00:00Z'
  },
  {
    id: 4,
    name: 'Jorge Pira',
    normalizedName: 'jorge pira',
    firstSeen: '2024-03-01',
    lastSeen: '2024-12-21',
    totalDaysWorked: 68,
    status: 'active',
    createdAt: '2024-03-01T00:00:00Z',
    updatedAt: '2024-12-21T00:00:00Z'
  },
  {
    id: 5,
    name: 'Escarleth Padilla',
    normalizedName: 'escarleth padilla',
    firstSeen: '2024-04-01',
    lastSeen: '2024-12-21',
    totalDaysWorked: 58,
    status: 'active',
    createdAt: '2024-04-01T00:00:00Z',
    updatedAt: '2024-12-21T00:00:00Z'
  },
  {
    id: 6,
    name: 'Jaqueline Curalon',
    normalizedName: 'jaqueline curalon',
    firstSeen: '2024-05-01',
    lastSeen: '2024-12-21',
    totalDaysWorked: 52,
    status: 'active',
    createdAt: '2024-05-01T00:00:00Z',
    updatedAt: '2024-12-21T00:00:00Z'
  },
  // Workers available in the pool (additional names)
  {
    id: 7,
    name: 'Eugenio Flores',
    normalizedName: 'eugenio flores',
    firstSeen: '2024-06-01',
    lastSeen: '2024-12-20',
    totalDaysWorked: 45,
    status: 'active',
    createdAt: '2024-06-01T00:00:00Z',
    updatedAt: '2024-12-20T00:00:00Z'
  },
  {
    id: 8,
    name: 'Luiz Espinoza',
    normalizedName: 'luiz espinoza',
    firstSeen: '2024-07-01',
    lastSeen: '2024-12-19',
    totalDaysWorked: 38,
    status: 'active',
    createdAt: '2024-07-01T00:00:00Z',
    updatedAt: '2024-12-19T00:00:00Z'
  },
  {
    id: 9,
    name: 'Byron Jarquin',
    normalizedName: 'byron jarquin',
    firstSeen: '2024-08-01',
    lastSeen: '2024-12-18',
    totalDaysWorked: 32,
    status: 'active',
    createdAt: '2024-08-01T00:00:00Z',
    updatedAt: '2024-12-18T00:00:00Z'
  },
  {
    id: 10,
    name: 'Hader J Roxas',
    normalizedName: 'hader j roxas',
    firstSeen: '2024-09-01',
    lastSeen: '2024-12-17',
    totalDaysWorked: 28,
    status: 'active',
    createdAt: '2024-09-01T00:00:00Z',
    updatedAt: '2024-12-17T00:00:00Z'
  },
  {
    id: 11,
    name: 'Elder Peralta',
    normalizedName: 'elder peralta',
    firstSeen: '2024-10-01',
    lastSeen: '2024-12-16',
    totalDaysWorked: 22,
    status: 'active',
    createdAt: '2024-10-01T00:00:00Z',
    updatedAt: '2024-12-16T00:00:00Z'
  },
  {
    id: 12,
    name: 'Emilio C',
    normalizedName: 'emilio c',
    firstSeen: '2024-11-01',
    lastSeen: '2024-12-15',
    totalDaysWorked: 18,
    status: 'active',
    createdAt: '2024-11-01T00:00:00Z',
    updatedAt: '2024-12-15T00:00:00Z'
  },
  {
    id: 13,
    name: 'David B',
    normalizedName: 'david b',
    firstSeen: '2024-11-15',
    lastSeen: '2024-12-14',
    totalDaysWorked: 15,
    status: 'active',
    createdAt: '2024-11-15T00:00:00Z',
    updatedAt: '2024-12-14T00:00:00Z'
  },
  {
    id: 14,
    name: 'O.M',
    normalizedName: 'o.m',
    firstSeen: '2024-12-01',
    lastSeen: '2024-12-13',
    totalDaysWorked: 8,
    status: 'active',
    createdAt: '2024-12-01T00:00:00Z',
    updatedAt: '2024-12-13T00:00:00Z'
  }
]

// Initial mock orders (will be reset to this state)
const initialOrders: Order[] = [
  {
    id: 1,
    title: 'Environmental Assessment - Site A',
    status: 'pending',
    amount: 5000,
    created_at: '2024-01-15T10:00:00Z',
    user_id: 1
  },
  {
    id: 2,
    title: 'Soil Testing - Industrial Park',
    status: 'active',
    amount: 3500,
    created_at: '2024-01-10T14:30:00Z',
    user_id: 1
  },
  {
    id: 3,
    title: 'Water Quality Analysis',
    status: 'completed',
    amount: 2000,
    created_at: '2024-01-05T09:15:00Z',
    user_id: 2
  }
]

// Mutable mock data (resets to initial state)
const mockOrders: Order[] = [...initialOrders]

// Contract-first approach: No legacy imports needed

// Enhanced mock orders with realistic data - Contract format (OrderResponse)
const mockOrdersData: OrderResponse[] = [
  {
    id: 1,
    title: 'Environmental Assessment - Site A',
    status: 'active',
    amount: 337.80,
    created_at: '2024-01-15T10:00:00Z',
    user_id: 1,
    project_id: 1,
    job_number: 'J2025-001',
    delivery_date: '2024-01-17',
    notes: 'PO: PO-2025-001',
    total_cost: 337.80,
    items: [
      {
        id: 1,
        product_id: 'TP7332Y',
        product_name: 'Test Product 1',
        quantity: 2,
        unit: 'EA',
        unit_price: 110.40,
        total_price: 220.80
      },
      {
        id: 2,
        product_id: 'COVTY400122-2',
        product_name: 'Test Product 2',
        quantity: 1,
        unit: 'Case',
        unit_price: 117.00,
        total_price: 117.00
      }
    ],
    project: {
      id: 1,
      name: 'Environmental Assessment - Site A',
      code: 'J2025-001'
    }
  },
  {
    id: 2,
    title: 'Soil Testing - Industrial Park',
    status: 'active',
    amount: 78.00,
    created_at: '2024-01-10T14:30:00Z',
    user_id: 1,
    project_id: 2,
    job_number: 'J2025-002',
    delivery_date: '2024-01-12',
    notes: 'PO: PO-2025-002',
    total_cost: 78.00,
    items: [
      {
        id: 3,
        product_id: 'ABRA6060',
        product_name: 'Test Product 3',
        quantity: 3,
        unit: 'DZ',
        unit_price: 26.00,
        total_price: 78.00
      }
    ],
    project: {
      id: 2,
      name: 'Soil Testing - Industrial Park',
      code: 'J2025-002'
    }
  },
  {
    id: 3,
    title: 'Water Quality Analysis',
    status: 'completed',
    amount: 428.00,
    created_at: '2024-01-05T09:15:00Z',
    user_id: 1,
    project_id: 3,
    job_number: 'J2025-003',
    delivery_date: '2024-01-09',
    notes: 'PO: PO-2025-003',
    total_cost: 428.00,
    items: [
      {
        id: 4,
        product_id: 'EUR930',
        product_name: 'Test Product 4',
        quantity: 1,
        unit: 'EA',
        unit_price: 428.00,
        total_price: 428.00
      }
    ],
    project: {
      id: 3,
      name: 'Water Quality Analysis',
      code: 'J2025-003'
    }
  }
]

// Replace the old mockOrders with enhanced data
const mockOrdersEnhanced: OrderResponse[] = [...mockOrdersData]

// Helper function to validate tokens in development environment
const isValidDevToken = (token: string): boolean => {
  // Accept both UUID tokens (production format) and dev-token format (legacy)
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token) || 
         token.startsWith('dev-token-')
}

// Mock API implementation following ApiClient interface
const mockApi: ApiClient = {
  async login(username: string, password: string): Promise<LoginResponse> {
    await new Promise(resolve => setTimeout(resolve, 300)) // Simulate network delay
    
    // For development/testing, use dedicated test users instead of production users
    const testUsers = getAllTestUsers()
    const user = testUsers.find(u => u.username === username)
    if (!user) {
      throw new Error('Invalid credentials')
    }
    
    // Validate password using test users
    const isValid = validateTestPassword(password, user.password_hash)
    if (!isValid) {
      throw new Error('Invalid credentials')
    }
    
    // Generate a proper UUID for the token (mock implementation)
    const mockUuid = `${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 6)}-4${Math.random().toString(16).slice(2, 5)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${Math.random().toString(16).slice(2, 5)}-${Math.random().toString(16).slice(2, 14)}`
    
    return {
      token: mockUuid,
      user: { id: user.id, username: user.username, role: user.role }
    }
  },

  async logout(): Promise<{ success: boolean; message?: string }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 50))
    
    // In development, logout always succeeds
    return {
      success: true,
      message: 'Logged out successfully'
    }
  },

  async getOrders(token: string): Promise<Order[]> {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    return [...mockOrders] // Return copy to prevent external mutation
  },

  async createOrder(token: string, orderData: { title: string; amount: number }): Promise<Order> {
    await new Promise(resolve => setTimeout(resolve, 400))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    const newOrder: Order = {
      id: Math.max(...mockOrders.map(o => o.id), 0) + 1,
      title: orderData.title,
      status: 'pending',
      amount: orderData.amount,
      created_at: new Date().toISOString(),
      user_id: 1
    }
    
    mockOrders.push(newOrder)
    return { ...newOrder } // Return copy
  },

  // Enhanced Orders API
  async getOrdersEnhanced(token: string, filters?: { status?: string; jobNumber?: string }): Promise<OrderResponse[]> {
    console.log('🔧 Mock getOrdersEnhanced:', filters)
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 200))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    let filtered = [...mockOrdersEnhanced]
    
    if (filters?.status) {
      // Map frontend status filter to contract status
      const contractStatusMap: Record<string, OrderResponse['status']> = {
        'pending': 'pending',
        'active': 'active',
        'completed': 'completed',
        'cancelled': 'cancelled'
      }
      const contractStatus = contractStatusMap[filters.status] || filters.status as OrderResponse['status']
              filtered = filtered.filter(order => order.status === contractStatus)
    }
    if (filters?.jobNumber) {
      // Filter by job_number (contract field)
      filtered = filtered.filter(order => order.job_number?.includes(filters.jobNumber!))
    }
    
    // Return contract format directly - no transformation needed
    return filtered
  },

  async createOrderEnhanced(token: string, orderData: CreateOrderRequest): Promise<OrderResponse> {
    console.log('🔧 Mock createOrderEnhanced:', orderData)
    
    // Validate request data using our contract
    const validatedData = debugValidation(CreateOrderRequestSchema, orderData, 'createOrderEnhanced mock request')
    if (!validatedData) {
      throw new Error('Invalid order data provided')
    }
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 400))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    // No longer need to create OrderItem[] - using contract format directly

    const newOrder: OrderResponse = {
      id: mockOrdersEnhanced.length + 1,
      title: validatedData.title,
      status: 'pending',
      amount: validatedData.items?.reduce((sum, item) => sum + item.total_price, 0) || 0,
      created_at: validatedData.created_at || new Date().toISOString(),
      user_id: 1,
      project_id: validatedData.project_id || null,
      job_number: validatedData.job_number || null,
      delivery_date: validatedData.delivery_date || null,
      notes: validatedData.notes || null,
      total_cost: validatedData.items?.reduce((sum, item) => sum + item.total_price, 0) || 0,
      items: validatedData.items?.map((item, index) => {
        const product = getProductById(item.product_id)
        return {
          id: mockOrdersEnhanced.length + index + 1,
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit: item.unit || product?.unit || 'EA', // Use unit from request or lookup from catalog
          unit_price: item.unit_price,
          total_price: item.total_price
        }
      }) || []
    }
    
    mockOrdersEnhanced.push(newOrder)
    
    // Return contract format directly - no transformation needed
    return newOrder
  },

  async updateOrderEnhanced(token: string, orderId: string, updates: UpdateOrderRequest): Promise<OrderResponse> {
    console.log('🔧 Mock updateOrderEnhanced:', { orderId, updates })
    
    // Validate request data using our contract
    const validatedUpdates = debugValidation(UpdateOrderRequestSchema, updates, 'updateOrderEnhanced mock request')
    if (!validatedUpdates) {
      throw new Error('Invalid update data provided')
    }
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    const orderIndex = mockOrdersEnhanced.findIndex(order => order.id === parseInt(orderId))
    if (orderIndex === -1) {
      throw new Error('Order not found')
    }
    
    const order = mockOrdersEnhanced[orderIndex]
    
    // Update items if provided (already in contract format)
    let updatedItems = order.items
    if (validatedUpdates.items) {
      updatedItems = validatedUpdates.items.map((item, index) => {
        const product = getProductById(item.product_id)
        return {
          id: order.items?.[index]?.id || (Date.now() + index),
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit: item.unit || product?.unit || 'EA', // Use unit from request or lookup from catalog
          unit_price: item.unit_price,
          total_price: item.total_price
        }
      })
    }

    // Calculate new total
    const newTotal = updatedItems?.reduce((sum, item) => sum + item.total_price, 0) || order.amount

    const updatedOrder: OrderResponse = {
      ...order,
      title: validatedUpdates.title || order.title,
      status: validatedUpdates.status || order.status,
      project_id: validatedUpdates.project_id !== undefined ? validatedUpdates.project_id : order.project_id,
      job_number: validatedUpdates.job_number !== undefined ? validatedUpdates.job_number : order.job_number,
      delivery_date: validatedUpdates.delivery_date !== undefined ? validatedUpdates.delivery_date : order.delivery_date,
      notes: validatedUpdates.notes !== undefined ? validatedUpdates.notes : order.notes,
      items: updatedItems,
      amount: newTotal,
      total_cost: newTotal
    }
    
    mockOrdersEnhanced[orderIndex] = updatedOrder
    
    // Return contract format directly - no transformation needed
    return updatedOrder
  },

  // Modern Orders API - Unified functionality (maps to enhanced methods)
  async getOrdersWithFilters(token: string, filters?: { status?: string; jobNumber?: string; projectId?: string }): Promise<OrderResponse[]> {
    // Map to existing enhanced method, ignoring projectId for now since it's not implemented in mock
    return this.getOrdersEnhanced(token, { status: filters?.status, jobNumber: filters?.jobNumber })
  },

  async createOrderWithItems(token: string, orderData: CreateOrderRequest): Promise<OrderResponse> {
    return this.createOrderEnhanced(token, orderData)
  },

  async updateOrder(token: string, orderId: string, updates: UpdateOrderRequest): Promise<OrderResponse> {
    return this.updateOrderEnhanced(token, orderId, updates)
  },

  async deleteOrder(token: string, orderId: string): Promise<{ message: string; id: number }> {
    console.log('🔧 Mock deleteOrder:', orderId)
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 200))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    const orderIndex = mockOrdersEnhanced.findIndex(order => order.id === parseInt(orderId))
    if (orderIndex === -1) {
      throw new Error('Order not found')
    }
    
    mockOrdersEnhanced.splice(orderIndex, 1)
    
    return { message: 'Order deleted successfully', id: parseInt(orderId) }
  },

  // Timesheet API - Mock implementation
  async getTimesheetToday(token: string): Promise<DailyTimesheet> {
    console.log('🔧 Mock getTimesheetToday')
    await new Promise(resolve => setTimeout(resolve, 200))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    const today = new Date().toISOString().split('T')[0]
    
    return {
      id: 1,
      date: today,
      payPeriodId: 1,
      jobSiteId: 1,
      status: 'draft',
      totalWorkers: 6,
      totalHours: 66, // 6 workers × 11 hours each (12 - 1 lunch)
      scanFileUrl: null,
      submittedBy: null,
      submittedAt: null,
      version: 1,
      // Draft management fields
      lastSavedAt: new Date().toISOString(),
      saveCount: 3,
      isDraft: true,
      entries: [
        {
          id: 1,
          timesheetId: 1,
          workerId: 1,
          worker: mockWorkers[0], // Diana Flores
          timeIn: '05:00',
          timeOut: '17:00',
          hoursWorked: 11, // 12 gross - 1 lunch
          createdAt: new Date().toISOString()
        },
        {
          id: 2,
          timesheetId: 1,
          workerId: 2,
          worker: mockWorkers[1], // Henry Picado
          timeIn: '05:00',
          timeOut: '17:00',
          hoursWorked: 11, // 12 gross - 1 lunch
          createdAt: new Date().toISOString()
        },
        {
          id: 3,
          timesheetId: 1,
          workerId: 3,
          worker: mockWorkers[2], // Nelson Perez
          timeIn: '05:00',
          timeOut: '17:00',
          hoursWorked: 11, // 12 gross - 1 lunch
          createdAt: new Date().toISOString()
        },
        {
          id: 4,
          timesheetId: 1,
          workerId: 4,
          worker: mockWorkers[3], // Jorge Pira
          timeIn: '05:00',
          timeOut: '17:00',
          hoursWorked: 11, // 12 gross - 1 lunch
          createdAt: new Date().toISOString()
        },
        {
          id: 5,
          timesheetId: 1,
          workerId: 5,
          worker: mockWorkers[4], // Escarleth Padilla
          timeIn: '05:00',
          timeOut: '17:00',
          hoursWorked: 11, // 12 gross - 1 lunch
          createdAt: new Date().toISOString()
        },
        {
          id: 6,
          timesheetId: 1,
          workerId: 6,
          worker: mockWorkers[5], // Jaqueline Curalon
          timeIn: '05:00',
          timeOut: '17:00',
          hoursWorked: 11, // 12 gross - 1 lunch
          createdAt: new Date().toISOString()
        }
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Submission workflow fields
      submissionId: null,
      overtimeHours: null,
      estimatedPayroll: null,
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
      managementNotes: null
    }
  },

  async searchWorkers(token: string, query: string): Promise<{ workers: Worker[]; total: number }> {
    console.log('🔧 Mock searchWorkers:', query)
    await new Promise(resolve => setTimeout(resolve, 150))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    if (!query || query.length < 2) {
      const activeWorkers = mockWorkers.filter(w => w.status === 'active').slice(0, 10)
      return { workers: activeWorkers, total: activeWorkers.length }
    }
    
    const normalizedQuery = query.toLowerCase().trim()
    
    const filteredWorkers = mockWorkers
      .filter(worker => 
        worker.status === 'active' && 
        worker.normalizedName.includes(normalizedQuery)
      )
      .sort((a, b) => {
        // Sort by how early the query appears in the name
        const aIndex = a.normalizedName.indexOf(normalizedQuery)
        const bIndex = b.normalizedName.indexOf(normalizedQuery)
        if (aIndex !== bIndex) return aIndex - bIndex
        
        // Then by name length (shorter names first)
        return a.name.length - b.name.length
      })
      .slice(0, 10) // Limit to 10 results
    
    return { workers: filteredWorkers, total: filteredWorkers.length }
  },

  async submitTimesheet(token: string, timesheet: Partial<DailyTimesheet>): Promise<DailyTimesheet> {
    console.log('🔧 Mock submitTimesheet:', timesheet)
    await new Promise(resolve => setTimeout(resolve, 400))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    // Respect the input status instead of hardcoding 'submitted'
    const status = timesheet.status || 'draft'
    const date = timesheet.date || new Date().toISOString().split('T')[0]
    
    // ✅ TRACK SUBMISSION: Update submission state for history refresh
    if (status === 'submitted') {
      submissionTracker.set(date, {
        status: 'submitted',
        submittedAt: new Date().toISOString()
      })
      console.log('📝 Tracked submission for date:', date)
    } else if (status === 'draft') {
      submissionTracker.set(date, {
        status: 'draft',
        lastSavedAt: new Date().toISOString()
      })
    }
    
    // Simulate submission or draft save
    const processedTimesheet: DailyTimesheet = {
      id: timesheet.id || 1,
      date: timesheet.date || new Date().toISOString().split('T')[0],
      payPeriodId: timesheet.payPeriodId || 1,
      jobSiteId: timesheet.jobSiteId || 1,
      status: status,
      totalWorkers: timesheet.entries?.length || 0,
      totalHours: timesheet.entries?.reduce((sum: number, entry) => sum + entry.hoursWorked, 0) || 0,
      scanFileUrl: timesheet.scanFileUrl || null,
      submittedBy: status === 'submitted' ? 1 : null, // Only set if actually submitted
      submittedAt: status === 'submitted' ? new Date().toISOString() : null, // Only set if actually submitted
      version: 1,
      // Draft management fields
      lastSavedAt: status === 'draft' ? new Date().toISOString() : null, // Only set for drafts
      saveCount: status === 'draft' ? (timesheet.saveCount || 0) + 1 : 0,
      isDraft: status === 'draft',
      entries: timesheet.entries || [],
      createdAt: timesheet.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Submission workflow fields
      submissionId: status === 'submitted' ? `TS-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}-001` : null,
      overtimeHours: status === 'submitted' ? 0 : null, // Calculate for submitted timesheets
      estimatedPayroll: status === 'submitted' ? 0 : null, // Calculate for submitted timesheets
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
      managementNotes: null
    }
    
    return processedTimesheet
  },

  // New dedicated draft save function
  async saveDraft(token: string, timesheet: Partial<DailyTimesheet>): Promise<DailyTimesheet> {
    console.log('🔧 Mock saveDraft:', timesheet)
    await new Promise(resolve => setTimeout(resolve, 200)) // Faster for draft saves
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    // Always save as draft
    const draftTimesheet: DailyTimesheet = {
      id: timesheet.id || 1,
      date: timesheet.date || new Date().toISOString().split('T')[0],
      payPeriodId: timesheet.payPeriodId || 1,
      jobSiteId: timesheet.jobSiteId || 1,
      status: 'draft',
      totalWorkers: timesheet.entries?.length || 0,
      totalHours: timesheet.entries?.reduce((sum: number, entry) => sum + entry.hoursWorked, 0) || 0,
      scanFileUrl: timesheet.scanFileUrl || null,
      submittedBy: null, // Never set for drafts
      submittedAt: null, // Never set for drafts
      version: 1,
      // Draft management fields
      lastSavedAt: new Date().toISOString(),
      saveCount: (timesheet.saveCount || 0) + 1,
      isDraft: true,
      entries: timesheet.entries || [],
      createdAt: timesheet.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Submission workflow fields (null for drafts)
      submissionId: null,
      overtimeHours: null,
      estimatedPayroll: null,
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
      managementNotes: null
    }
    
    return draftTimesheet
  },

  async createWorker(token: string, workerData: { name: string }): Promise<{ worker: Worker; message?: string }> {
    console.log('🔧 Mock createWorker:', workerData)
    await new Promise(resolve => setTimeout(resolve, 300))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    const newWorker: Worker = {
      id: Math.max(...mockWorkers.map(w => w.id), 0) + 1,
      name: workerData.name,
      normalizedName: workerData.name.toLowerCase().trim(),
      firstSeen: new Date().toISOString().split('T')[0],
      lastSeen: new Date().toISOString().split('T')[0],
      totalDaysWorked: 1,
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    mockWorkers.push(newWorker)
    return {
      worker: newWorker,
      message: `Worker "${newWorker.name}" created successfully`
    }
  },

  // Export order as PDF
  async exportOrderPDF(token: string, orderId: number): Promise<OrderResponse> {
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate PDF generation time
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    const order = mockOrdersEnhanced.find(o => o.id === orderId)
    if (!order) {
      throw new Error('Order not found')
    }
    
    // Return the order data for frontend PDF generation
    return order
  },

  // Timesheet Reports API methods
  async getTimesheetReports(token: string, filters?: {
    startDate?: string
    endDate?: string
    employee?: string
    status?: 'submitted' | 'approved' | 'rejected'
    limit?: number
    offset?: number
  }) {
    console.log('🔧 Mock getTimesheetReports:', filters)
    await new Promise(resolve => setTimeout(resolve, 200))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    // Generate mock timesheet reports for the last 30 days
    const allTimesheets = []
    const today = new Date()
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)
      const dayOfWeek = date.getDay()
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        continue
      }
      
      const dateString = date.toISOString().split('T')[0]
      
      // Generate realistic data for each day
      const totalWorkers = 6 + Math.floor(Math.random() * 4) // 6-9 workers
      const hoursPerWorker = 7.5 + Math.random() * 2 // 7.5-9.5 hours each
      const totalHours = totalWorkers * hoursPerWorker
      const overtimeHours = totalHours > totalWorkers * 8 ? totalHours - (totalWorkers * 8) : 0
      
      // Status distribution: 70% submitted, 20% approved, 10% rejected
      const rand = Math.random()
      const status: 'submitted' | 'approved' | 'rejected' = rand < 0.1 ? 'rejected' : rand < 0.3 ? 'approved' : 'submitted'
      
      // Random submission time within work hours
      const submittedAtDate = new Date(date)
      submittedAtDate.setHours(15 + Math.floor(Math.random() * 3), Math.floor(Math.random() * 60))
      const submittedAt = submittedAtDate.toISOString()
      
      const submissionId = `TS${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`

      allTimesheets.push({
        date: dateString,
        status,
        totalWorkers,
        totalHours: Math.round(totalHours * 10) / 10,
        overtimeHours: Math.round(overtimeHours * 10) / 10,
        lastSavedAt: null, // Not a draft
        submittedAt,
        submittedBy: 'wade@zeigler.com',
        submissionId,
        canEdit: false,
        canView: true,
        hasAttachments: Math.random() < 0.7 // 70% have attachments
      })
    }
    
    // Apply filters
    let filteredTimesheets = allTimesheets
    
    // Date range filter
    if (filters?.startDate) {
      filteredTimesheets = filteredTimesheets.filter(t => t.date >= filters.startDate!)
    }
    if (filters?.endDate) {
      filteredTimesheets = filteredTimesheets.filter(t => t.date <= filters.endDate!)
    }
    
    // Employee filter
    if (filters?.employee) {
      filteredTimesheets = filteredTimesheets.filter(t => 
        t.submittedBy?.toLowerCase().includes(filters.employee!.toLowerCase())
      )
    }
    
    // Status filter
    if (filters?.status) {
      filteredTimesheets = filteredTimesheets.filter(t => t.status === filters.status)
    }
    
    // Sort by date descending (most recent first)
    filteredTimesheets.sort((a, b) => b.date.localeCompare(a.date))
    
    const total = filteredTimesheets.length
    
    // Apply pagination
    const offset = filters?.offset || 0
    const limit = filters?.limit || 50
    const paginatedTimesheets = filteredTimesheets.slice(offset, offset + limit)
    
    return {
      timesheets: paginatedTimesheets,
      total,
      hasMore: offset + limit < total,
      page: Math.floor(offset / limit),
      limit
    }
  },

  // ✅ FIX: Timesheet History API - provide mock data for development
  async getTimesheetHistory(token: string, days: number = 7): Promise<{ timesheets: TimesheetHistoryItem[]; total: number }> {
    console.log('🔧 Mock getTimesheetHistory:', { days })
    await new Promise(resolve => setTimeout(resolve, 200))
    
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    
    // Generate mock history data for the last few days
    const today = new Date()
    const timesheets: TimesheetHistoryItem[] = []
    
    for (let i = 0; i < Math.min(days, 7); i++) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)
      const dateString = date.toISOString().split('T')[0]
      
      // Skip weekends
      const dayOfWeek = date.getDay()
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        continue
      }
      
             let status: 'draft' | 'submitted' | 'approved' | 'rejected'
       let lastSavedAt: string | null = null
       let submittedAt: string | null = null
       
       // ✅ CHECK TRACKED STATE: Use actual submission state if available
       const trackedState = submissionTracker.get(dateString)
       if (trackedState) {
         status = trackedState.status
         lastSavedAt = trackedState.lastSavedAt || null
         submittedAt = trackedState.submittedAt || null
         console.log('📊 Using tracked state for', dateString, ':', trackedState)
       } else if (i === 0) {
         // Today - start as draft if no tracked state
         status = 'draft'
         lastSavedAt = new Date().toISOString()
       } else if (i === 1) {
         // Yesterday - submitted
         status = 'submitted'
         const submitTime = new Date(date)
         submitTime.setHours(16, 30, 0, 0) // Submitted at 4:30 PM
         submittedAt = submitTime.toISOString()
       } else {
         // Older days - mix of submitted/approved
         status = Math.random() > 0.5 ? 'submitted' : 'approved'
         const submitTime = new Date(date)
         submitTime.setHours(15 + Math.floor(Math.random() * 3), Math.floor(Math.random() * 60), 0, 0)
         submittedAt = submitTime.toISOString()
       }
      
             timesheets.push({
         date: dateString,
         status,
         totalWorkers: 6,
         totalHours: 66.0, // 6 workers × 11 hours (12 - 1 lunch)
         lastSavedAt,
         submittedAt
       })
    }
    
    return {
      timesheets,
      total: timesheets.length
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getTimesheetByDate(token: string, date: string): Promise<DailyTimesheet> {
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    // Fallback to production endpoint
    throw new Error('Use production endpoint /api/timesheet-by-date instead')
  },

  // ✅ STUB: ApproveTimesheet API - using production endpoint instead  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async approveTimesheet(token: string, submissionId: string, action: 'approve' | 'reject', notes?: string) {
    if (!isValidDevToken(token)) {
      throw new Error('Invalid token')
    }
    // Fallback to production endpoint
    throw new Error('Use production endpoint instead')
  }
}

// Development utilities implementation
const devUtils: DevUtilities = {
  resetMockData(): void {
    mockOrders.splice(0, mockOrders.length, ...initialOrders)
    console.log('🔄 Mock data reset to initial state')
  },

  getCurrentMockState(): { users: Array<Record<string, unknown>>, orders: Order[] } {
    return {
      users: getAllTestUsers().map(user => ({ ...user })) as Array<Record<string, unknown>>,
      orders: [...mockOrders]
    }
  }
}

// Export both API and utilities
export const devApi = mockApi
export { devUtils }

// Note: Dev utilities can be imported directly in tests/development as needed
// import { devUtils } from './api-dev' 

// Contract-first approach: No transformation needed!
// Mock data is already in OrderResponse format 